import { useCallback, useEffect } from 'react'

import { Flex, Menu, Square, Text } from '@chakra-ui/react'
import { DownloadIcon } from '@editor/chakra-ui-icons'
import { overlayCanvasOnBuffer } from '@editor/services/exports/BlueprintImageExport'
import { useAppDispatch, useAppSelector } from '@editor/store/app'

import { BLUEPRINT_RENDER_LOOP_FLAGS } from '../components/RenderLoop'
import { ExportState, exportDrawings, resetExportDrawings } from '../store/drawings'

/**
 * Blueprint Image Export Flow:
 * 1. Place a 2D canvas (via konva) on top of the 3D canvas.
 *    This canvas will have `display: none` (it does not need to be visible to render).
 *    It will remain idle until export is initiated.
 * 2. On export button clicked, it will;
 *     1. Prep the 2D canvas (resize it based on the 3D canvas)
 *        - Canvas2D component.
 *     2. Process data for 2D presentation (2D coordinates are calculated,
 *        and mm to pixel ratio + zoom scale stored as well for later use).
 *        Most data are converted from world space to screen space using ortho camera as ref,
 *        but a few uses direct HTML query to figure out where they are on the screen.
 *        - This is done in Drawings2DManager component.
 *     3. Create a dummy render target for Three.js to render into.
 *        Turn off grid and render onto the dummy target.
 *        Read from the dummy target into a buffer.
 *        - This is done in RenderLoop component.
 *     4. After 1 second, a third dummy canvas created, 3D export drawn first (from buffer)
 *        then 2D export overlaid on top.
 *        - Still in Drawings2DManager component.
 *     5. The dummy is then exported with `toDataURL()`.
 *     6. Initiate download process.
 */

/**
 * Blueprint Image Export Button component
 */
const BlueprintImageExportButton = () => {
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const selectedBlueprint = useAppSelector((state) => state.blueprint.editor.selectedBlueprint)
  const document2d = useAppSelector((state) => state.blueprint.drawings.document2d)

  const canExport = Boolean(project?.project_id && inspectionArea?.inspection_area_id && selectedBlueprint?.sort_key)

  /**
   * Handle export button click
   */
  const handleExport = useCallback(() => {
    dispatch(exportDrawings(ExportState.PrepCanvas))
  }, [dispatch])

  /**
   * Once the export prep has completed, trigger the image export process.
   */
  useEffect(() => {
    if (document2d.exportState !== ExportState.Exporting || !selectedBlueprint?.sort_key) return

    const canvas3d = document.querySelector('#canvasBox canvas')
    const canvas2d = document.querySelector('#canvas-2d canvas')
    if (!(canvas3d instanceof HTMLCanvasElement) || !(canvas2d instanceof HTMLCanvasElement)) return

    // kick off the render loop to capture the render buffer
    BLUEPRINT_RENDER_LOOP_FLAGS.showGrid = false

    // Poll for the render buffer to be ready
    const checkBufferReady = async () => {
      if (BLUEPRINT_RENDER_LOOP_FLAGS.buffer) {
        if (BLUEPRINT_RENDER_LOOP_FLAGS.buffer.length !== canvas3d.width * canvas3d.height * 4) {
          return
        }

        // Prep canvas
        const exportCanvas = document.createElement('canvas')
        exportCanvas.width = canvas3d.width
        exportCanvas.height = canvas3d.height

        // Generate the final image by overlaying the 2D canvas on top of the 3D render buffer then downloading it
        const dataUrl = await overlayCanvasOnBuffer(exportCanvas, BLUEPRINT_RENDER_LOOP_FLAGS.buffer, canvas2d)
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `${selectedBlueprint.name}設計図面.png`
        link.click()

        // Reset and clean up
        BLUEPRINT_RENDER_LOOP_FLAGS.showGrid = true
        BLUEPRINT_RENDER_LOOP_FLAGS.buffer = null
        dispatch(resetExportDrawings())
        return
      }

      requestAnimationFrame(checkBufferReady)
    }

    void checkBufferReady()
  }, [document2d, selectedBlueprint, dispatch])

  return (
    <Menu.Item
      value="blueprint-image-export"
      disabled={!canExport}
      opacity={!canExport ? 0.6 : 1}
      cursor={!canExport ? 'not-allowed' : 'pointer'}
      onClick={canExport ? handleExport : undefined}
    >
      <Flex direction="row">
        <Square fontSize={{ base: 'xl', xl: 'lg' }} className="chakra-menu__icon-wrapper">
          <DownloadIcon />
        </Square>
        <Text>画像出力</Text>
      </Flex>
    </Menu.Item>
  )
}

export default BlueprintImageExportButton
